import React, { useState, useRef, useEffect } from 'react';
import { useStyles } from '@webapps/styles';
import { Button } from '@material-ui/core';
import { EditRounded } from '@material-ui/icons';
import { calculateHeightPx, calculateTopPx, getMinutesBetween, toMinutes, toHourStr, addHourToString, formattedTime } from '../utils/services';
import QrCodeIcon from 'components/Icons/QrCodeIcon';

// Convertit une position Y en heure, selon la grille
function getHourFromY(clientY, containerTop, pxPerHour = 100, referenceHour = 8) {
  const minutesFromTop = (clientY - containerTop) * 60 / pxPerHour;
  const hour = Math.floor(referenceHour + minutesFromTop / 60);
  const minute = Math.round(minutesFromTop % 60);
  return `${hour}h${minute.toString().padStart(2, '0')}`;
}

const ScheduleRowLeft = ({ hour, index }) => {
  const { classes } = useStyles(styles);

  return (
    <div className={classes.hourContainer}
      style={{
        borderTopWidth: index === 0 ? "1px" : "0px",
        borderBottomWidth: "1px"
      }}
    >
      <div className={classes.hour}>{hour}</div>
    </div>
  )
}

const ScheduleBackgroundGrid = ({ startHour, endHour, firstClassHour = "8h00", rowHeightPx }) => {
  const minutes = getMinutesBetween(startHour, endHour);
  const numberOfRows = Math.floor(minutes / 5);

  return (
    <div style={{
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "100%",
      zIndex: 1, // en-dessous des cours et emptyLines
      pointerEvents: "none"
    }}>
      {Array.from({ length: numberOfRows }).map((_, i) => {
        const isZero = i % 12 === 0;
        const isThirty = (i * 5) % 60 === 30;
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              top: `${i * rowHeightPx}px`,
              left: 0,
              right: 0,
              height: 0,
              borderTop: isZero || isThirty ? "1px solid #c0d9ff" : "none"
            }}
          />
        );
      })}
    </div>
  );
};


const ScheduleRowRight = ({ index, course, firstClassHour = "8h00", editCourse, canModify, from }) => {
  const { classes, cx } = useStyles(styles);

  console.log("ScheduleRowRight course ", course);

  const topPx = calculateTopPx({
    startHour: course.startTime,
    referenceHour: firstClassHour
  });

  const heightPx = calculateHeightPx({
    startHour: course.startTime,
    endHour: course.endTime
  });

  const style = {
    position: "absolute",
    top: `${topPx}px`,
    height: `${heightPx}px`
  };

  const startHourMinutes = parseInt(course.startTime.split(":")[1]);
  const endHourMinutes = parseInt(course.endTime.split(":")[1]);

  const isZero = startHourMinutes % 60 === 0; // Heures pleines (0mn)
  const isThirty = startHourMinutes % 60 === 30; // Demi-heures (30mn)

  const isZeroEnd = endHourMinutes % 60 === 0; // Heures pleines (0mn)

  return (
    <div className={classes.courseContainer}
      style={{
        ...style,
        background: "linear-gradient(90deg, #f3f7ff 0%, #c0d9ff 100%)",
        border: "1px solid #75A8FB",
      }}
    >
      <div className={classes.courseInfoContainer}
        style={{
          marginLeft: from === "student" ? 0 : 80
        }}
      >
        <div className={classes.startHour}>{formattedTime(course.startTime)} - {formattedTime(course.endTime)}</div>
        <div className={classes.title}>{course.title}</div>
        <div className={classes.location}>{course.room.label} | {course.teacher.fullname}</div>
      </div>
      {canModify && (
        <Button className={classes.editButton}
          onClick={() => editCourse({
            id: course.id,
            startTime: course.startTime,
            endTime: course.endTime,
            title: course.title,
            room: course.room,
            teacher: course.teacher
          })}
        >
          <EditRounded className={classes.editIcon} />
        </Button>
      )}
    </div>
  )
}

const EmptyCourse = ({
  startHour,
  endHour,
  firstClassHour = "8h00",
  editCourse,
  resetEmptyCoursesSignal,
  canModify
}) => {
  const { classes, cx } = useStyles(styles);
  const ref = useRef();


  // States
  const [selecting, setSelecting] = useState(false);

  // Indices de sélection (et leurs refs synchrones)
  const [selectionStartIndex, setSelectionStartIndex] = useState(null);
  const [selectionEndIndex, setSelectionEndIndex] = useState(null);
  const selectionStartIndexRef = useRef(null);
  const selectionEndIndexRef = useRef(null);


  const setSelectionStartIndexSafe = (val) => {
    selectionStartIndexRef.current = val;
    setSelectionStartIndex(val);
  };

  const setSelectionEndIndexSafe = (val) => {
    selectionEndIndexRef.current = val;
    setSelectionEndIndex(val);
  };

  useEffect(() => {
    console.log("resetEmptyCoursesSignal : ", resetEmptyCoursesSignal);
    setSelecting(false);
    setSelectionStartIndexSafe(null);
    setSelectionEndIndexSafe(null);
    console.log("resetEmptyCoursesSignal data : ", {
      selecting,
      selectionStartIndex,
      selectionEndIndex
    });

  }, [resetEmptyCoursesSignal]);

  // Pour le drag
  const selectingRef = useRef();
  selectingRef.current = selecting;

  const topPx = calculateTopPx({
    startHour: startHour,
    referenceHour: firstClassHour
  });
  const heightPx = calculateHeightPx({
    startHour: startHour,
    endHour: endHour
  });

  const style = {
    position: "absolute",
    top: `${topPx}px`,
    height: `${heightPx}px`,
    display: "flex",
    flexDirection: "column",
    padding: "0px",
    userSelect: "none"
  };

  const minutes = getMinutesBetween(startHour, endHour);
  const startHourMinutes = parseInt(startHour.split("h")[1]);
  console.log("startHourMinutes : ", startHourMinutes);

  const numberOfRows = Math.floor(minutes / 5);
  const rowHeightPx = heightPx / numberOfRows;

  // Calcul index à partir de la position Y relative au conteneur
  const getIndexFromY = (clientY) => {
    const containerTop = ref.current?.getBoundingClientRect().top || 0;
    const y = clientY - containerTop;
    let idx = Math.floor(y / rowHeightPx);
    if (idx < 0) idx = 0;
    if (idx >= numberOfRows) idx = numberOfRows - 1;
    return idx;
  };

  // EVENTS
  const handleMouseDown = (e) => {
    if (!canModify) return;
    if (e.button !== 0) return; // Only left click
    const idx = getIndexFromY(e.clientY);
    setSelecting(true);
    setSelectionStartIndexSafe(idx);
    setSelectionEndIndexSafe(idx);

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!selectingRef.current) return;
    const idx = getIndexFromY(e.clientY);
    setSelectionEndIndexSafe(idx);
  };

  const handleMouseUp = (e) => {
    if (!canModify) return;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);

    // On capture les valeurs synchrones via ref
    const startIdx = selectionStartIndexRef.current;
    const endIdx = selectionEndIndexRef.current;


    if (selectingRef.current && startIdx !== null && endIdx !== null) {
      const minIndex = Math.min(startIdx, endIdx);
      const maxIndex = Math.max(startIdx, endIdx) + 1;
      const startMin = toMinutes(startHour) + minIndex * 5;
      const endMin = toMinutes(startHour) + maxIndex * 5;
      const startTime = toHourStr(startMin);
      const endTime = toHourStr(endMin);

      if (editCourse) editCourse({ startTime, endTime, minuteInterval: (maxIndex - minIndex) * 5 });
    }

    // Reset immédiatement les états et refs
    setSelecting(false);
    setSelectionStartIndexSafe(null);
    setSelectionEndIndexSafe(null);
  };

  // Génère les lignes pour le rendu visuel
  const rowsAll5Minutes = [];
  for (let i = 0; i < numberOfRows; i++) {
    rowsAll5Minutes.push(i);
  }

  console.log("rowsAll5Minutes : ", rowsAll5Minutes);


  return (
    <div
      ref={ref}
      className={classes.courseContainer}
      style={style}
      onMouseDown={handleMouseDown}
    >
      <div className={classes.emptyLineContainer}>
        {rowsAll5Minutes.map((index, i) => {
          const isSelected = selecting && selectionStartIndex !== null && selectionEndIndex !== null &&
            i >= Math.min(selectionStartIndex, selectionEndIndex) &&
            i <= Math.max(selectionStartIndex, selectionEndIndex);

          const start = addHourToString(startHour, 0, index * 5);
          const end = addHourToString(startHour, 0, (index + 1) * 5);
          const totalMinutes = index * 5 + startHourMinutes;
          const isZero = totalMinutes % 60 === 0; // Heures pleines (0mn)
          const isThirty = totalMinutes % 60 === 30; // Demi-heures (30mn)

          // Debug pour les premières lignes
          if (i < 5) {
            console.log(`Ligne ${i}: index=${index}, startHourMinutes=${startHourMinutes}, totalMinutes=${totalMinutes}, isZero=${isZero}, isThirty=${isThirty}, start=${start}`);
          }

          return (
            <div
              key={i}
              className={cx(classes.emptyLine, !canModify && classes.noHover)}
              style={{
                height: `${rowHeightPx}px`,
                width: "100%",
                backgroundColor: isSelected && canModify ? "rgba(0,96,249,0.2)" : "transparent",
                borderTop: (isZero || isThirty)
                  ? "1px solid #c0d9ff"
                  : (isSelected && canModify ? "1px solid #0060f9" : "none"),
                borderRight: isSelected && canModify ? "1px solid #0060f9" : "none",
                borderBottom: isSelected && canModify ? "1px solid #0060f9" : "none",
                borderLeft: isSelected && canModify ? "1px solid #0060f9" : "none",
                position: "relative",
                cursor: canModify ? "pointer" : "default"
              }}
            >
              <div
                className={selecting ? "line-hover-info-hidden" : "line-hover-info"}
                style={{
                  height: `${rowHeightPx}px`,
                  background: "rgba(0,96,249,0.2)",
                  border: "1px solid #0060f9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 2,
                  fontWeight: "bold",
                  pointerEvents: "none",
                  display: "none"
                }}

              >
                <span style={{
                  color: "#0060f9",
                  background: "#fff",
                  padding: "2px 8px",
                  borderRadius: 6,
                  fontSize: 13,
                  boxShadow: "0 1px 4px rgba(0,0,0,0.07)"
                }}>
                  {start} — {end}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      {/* Affichage de la période sélectionnée */}
      {selecting && selectionStartIndex !== null && selectionEndIndex !== null && (() => {
        const minIndex = Math.min(selectionStartIndex, selectionEndIndex);
        const maxIndex = Math.max(selectionStartIndex, selectionEndIndex) + 1;
        const startMin = toMinutes(startHour) + minIndex * 5;
        const endMin = toMinutes(startHour) + maxIndex * 5;
        const startTime = toHourStr(startMin);
        const endTime = toHourStr(endMin);
        const top = minIndex * rowHeightPx;
        const height = (maxIndex - minIndex) * rowHeightPx;
        return (
          <div
            style={{
              position: "absolute",
              top,
              height,
              left: 0,
              right: 0,
              background: "rgba(0,96,249,0.2)",
              border: "1px solid #0060f9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2,
              fontWeight: "bold",
              pointerEvents: "none"
            }}
          >
            <span style={{
              color: "#0060f9",
              background: "#fff",
              padding: "2px 8px",
              borderRadius: 6,
              fontSize: 13,
              boxShadow: "0 1px 4px rgba(0,0,0,0.07)"
            }}>
              {startTime} — {endTime}
            </span>
          </div>
        );
      })()}
    </div>
  )
}

const QRCodeChip = ({ qrCode, firstClassHour = "8h00" }) => {
  const { classes, cx } = useStyles(styles);

  const formattedGeneratedAt = formattedTime(qrCode.generatedAt);
  const topPx = calculateTopPx({
    startHour: formattedGeneratedAt,
    referenceHour: firstClassHour
  });
  return (
    <div className={classes.qrCodeChip} style={{ top: `${topPx}px` }}>
      <QrCodeIcon
        className={classes.qrCodeIcon}
      />
      <div className={classes.qrCodeInfo}>
        <div className={classes.qrCodeGeneratedAt}>{formattedGeneratedAt}</div>
        <div className={classes.qrCodeStudents}>{qrCode.signedCount} / {qrCode.totalStudents}</div>
      </div>
    </div>
  )
}

export default function ScheduleRows({ hours, courses, from, allQRCode, emptyIntervals, editCourse, resetEmptyCoursesSignal, canModify = true, disabledConfig = { disabled: false, message: "" } }) {
  const { classes, cx } = useStyles(styles);

  const isLastHour = (hour) => hour === hours[hours.length - 1];
  console.log("emptyIntervals : ", emptyIntervals);


  return (
    <div className={cx(classes.scheduleContainer, disabledConfig.disabled && classes.disabled)} style={{ position: 'relative' }}>
      {disabledConfig.disabled && (
        <div className={classes.disabledOverlay}>
          <div className={classes.disabledMessage}>{disabledConfig.message}</div>
        </div>
      )}
      {
        hours.map((hour, index) => (
          !isLastHour(hour) && <ScheduleRowLeft hour={hour} key={index} index={index} />
        ))
      }
      {
        courses.map((course, index) => (
          <ScheduleRowRight
            key={index}
            index={index}
            course={course}
            editCourse={editCourse}
            canModify={canModify && !disabledConfig.disabled}
            from={from}
          />
        ))
      }
      {
        canModify && allQRCode.length > 0 && allQRCode.map((qrCode, index) => (
          <QRCodeChip
            key={index}
            qrCode={qrCode}
          />
        ))
      }
      {
        emptyIntervals.map((interval, index) => (
          <EmptyCourse
            key={index}
            startHour={interval.startTime}
            endHour={interval.endTime}
            editCourse={editCourse}
            resetEmptyCoursesSignal={resetEmptyCoursesSignal}
            canModify={canModify && !disabledConfig.disabled}
          />
        ))
      }
    </div>
  )
}

const styles = ({ spacing }) => ({
  rowContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    position: "relative",
    width: "100%",
  },
  "framerDiv": {
    alignItems: "flex-start",
    display: "flex",
    flexDirection: "row",
    height: "107px",
    justifyContent: "flex-start",
    position: "relative",
    width: "100%",
    overflow: "hidden",
  },
  scheduleContainer: {
    display: 'flex',
    flexDirection: 'column',
    position: "relative",
  },
  hourContainer: {
    borderRightWidth: "1px",
    borderLeftWidth: "0px",
    borderColor: "#75A8FB",
    borderStyle: "solid",
    alignContent: "center",
    display: "flex",
    flexDirection: "row",
    height: "100%",
    padding: "39.5px 20px",
    position: "relative",
    width: "min-content",
    overflow: "hidden",
    placeItems: "center",
    zIndex: 10,
  },
  hour: {
    fontFamily: "\"Roboto\", sans-serif",
    fontSize: "14px",
    textAlignment: "left",
    color: "#5d75a8",
    width: "40px",
    height: "20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  courseContainer: {
    alignItems: "center",
    display: "flex",
    flexDirection: "row",
    flexGrow: "1",
    height: "100%",
    justifyContent: "flex-start",
    padding: "16px",
    position: "absolute",
    top: "0",
    left: "80px",
    width: "calc(100% - 80px)",
    overflow: "hidden",
    userSelect: "none",

  },
  emptyLineContainer: {
    position: "relative",
    width: "100%",
    height: "100%",
  },
  emptyLine: {
    position: "absolute",
    width: "100%",

    "&:hover": {
      backgroundColor: "rgba(0,96,249,0.2) !important",

      ".line-hover-info": {
        display: "flex !important"
      }
    }

    // backgroundColor: "#c0d9ff",
  },
  courseInfoContainer: {
    alignItems: "flex-start",
    display: "flex",
    flexDirection: "column",
    flexGrow: "1",
    height: "min-content",
    justifyContent: "flex-start",
    position: "relative",
    gap: "4px",
    overflow: "hidden",
  },
  startHour: {
    fontFamily: "\"Roboto\", sans-serif",
    fontSize: "13px",
    textAlignment: "left",
    color: "#0060f9",
  },
  title: {
    fontFamily: "\"Montserrat\", sans-serif",
    fontSize: "14px",
    fontWeight: "500",
    textAlignment: "left",
    color: "#152652",
  },
  location: {
    fontFamily: "\"Roboto\", sans-serif",
    fontSize: "12px",
    fontStyle: "italic",
    textAlignment: "left",
    color: "#5d75a8",
  },
  editButton: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    height: "26px",
    width: "26px",
    backgroundColor: "#F3F7FF",
    border: "1px solid #C0D9FF",
    transition: "none",
    "&:hover": {
      border: "1px solid #0060f9",
      backgroundColor: "#F3F7FF",
      color: "#0060f9",
      fill: "#0060f9",
    },
    '&.MuiButton-root': {
      minWidth: "26px",
    },
  },
  editIcon: {
    height: "16px",
    position: "relative",
    width: "16px",
    fill: "#5d75a8",
    '&:hover': {},
  },
  disabled: {
    filter: "grayscale(1)",
    pointerEvents: "none",
    opacity: 0.7,
    position: "relative",
  },
  disabledOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(255,255,255,0.7)",
    zIndex: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "auto",
  },
  disabledMessage: {
    color: "#152652",
    fontWeight: 600,
    fontSize: 18,
    background: "white",
    borderRadius: 8,
    padding: "16px 32px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
    zIndex: 21,
  },
  noHover: {
    '&:hover': {
      backgroundColor: 'inherit !important',
      border: 'none !important',
      cursor: 'default !important',
      boxShadow: 'none !important',
      filter: 'none !important',
      '.line-hover-info': {
        display: 'none !important'
      }
    },
    cursor: 'default !important',
  },
  qrCodeChip: {
    position: 'absolute',
    left: '65px',
    width: '100px',
    height: 'fit-content',
    padding: '10px 5px',
    backgroundColor: '#DBFAD8',
    color: "#008000",
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    // justifyContent: 'center',
    fontSize: '13px',
    fontWeight: '500',
    transform: 'translateY(-50%)',
  },
  qrCodeIcon: {
    width: '19px',
    height: '19px',
    marginRight: '4px',
    color: "#008000",
    fill: "#008000",
  },
});
